import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAccount } from './AccountContext';
import { useAuth } from './AuthContext';

/**
 * Which product shell the app is presenting: PostMaster/reach (social planning
 * and publishing), TinyCMS (the content library — articles, media, MCP),
 * Collect (collection management — capture, catalogue, value, list).
 *
 * The `atomizer` id is the TinyCMS shell: Today, Content, Site, Add-ons.
 * Atomizer itself is the add-on on that shell — Research, Assembler, and
 * atomizing an article — and an account that already has `atomizer` has the
 * add-on. `REACT_APP_PRODUCT=tinycms` is the same shell. The id stays
 * `atomizer` so existing entitlements do not move.
 *
 * Resolution order:
 *   1. Build pin — REACT_APP_PRODUCT ('reach' | 'atomizer' | 'collect'), for
 *      single-product deployments (each domain ships a pinned build).
 *   2. Hostname — a host containing "atomizer", "reach" or "collect" selects
 *      that shell.
 *   3. Saved choice — the in-app switcher persists to localStorage.
 *   4. Entitlements — an account with exactly one product gets that shell.
 *   5. Default — reach.
 */
export type ProductId = 'reach' | 'atomizer' | 'collect' | 'tinycms';

/** Every product id, in the order shells are offered. */
export const PRODUCT_IDS: ProductId[] = ['reach', 'atomizer', 'collect', 'tinycms'];

/** Customer-facing brand names. `atomizer` is shown as TinyCMS; the add-on
 *  features (Research, Assembler, atomization) keep the Atomizer name. */
export const PRODUCT_NAMES: Record<ProductId, string> = {
  reach: 'PostMaster',
  atomizer: 'TinyCMS',
  collect: 'Collect',
  tinycms: 'TinyCMS',
};

/** Landing route of each shell. */
export const PRODUCT_HOME: Record<ProductId, string> = {
  reach: '/',
  atomizer: '/today',
  collect: '/collection',
  // Same shell as atomizer. Kept so an old saved choice still resolves.
  tinycms: '/today',
};

const STORAGE_KEY = 'smagent.product';

/**
 * Map a brand name or internal id to a product id. Both spellings are accepted
 * everywhere a product is named, so REACT_APP_PRODUCT=postmaster and
 * REACT_APP_PRODUCT=reach mean the same thing.
 */
export function toProductId(value: string | undefined | null): ProductId | null {
  const v = (value || '').toLowerCase().trim();
  if (v === 'reach' || v === 'postmaster') return 'reach';
  if (v === 'atomizer' || v === 'tinycms' || v === 'cms') return 'atomizer';
  if (v === 'collect' || v === 'collector') return 'collect';
  return null;
}

/** Build-time or hostname pin; null when this build serves both shells. */
export function getProductPin(): ProductId | null {
  const fromEnv = toProductId(process.env.REACT_APP_PRODUCT);
  if (fromEnv) return fromEnv;
  const host = window.location.hostname.toLowerCase();
  if (host.includes('atomizer') || host.includes('tinycms')) return 'atomizer';
  if (host.includes('collect')) return 'collect';
  if (host.includes('postmaster') || host.includes('reach')) return 'reach';
  return null;
}

interface ProductContextValue {
  /** The active shell. */
  product: ProductId;
  /** Products the current account is entitled to. */
  entitled: ProductId[];
  /** True while entitlements are still loading. */
  isLoading: boolean;
  /** True when this build/domain serves a single product (no switcher). */
  isPinned: boolean;
  /** Switch shells (no-op when pinned). */
  setProduct: (product: ProductId) => void;
}

const ProductContext = createContext<ProductContextValue | null>(null);

export const ProductProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { selectedAccount, isLoading: accountsLoading } = useAccount();
  const pin = getProductPin();
  const loading = accountsLoading;

  // Entitlements follow the selected account (not the first membership).
  // Accounts predating the entitlement rollout (products missing) keep access
  // to both shells.
  const products = selectedAccount?.products;
  // Accounts predating the entitlement rollout keep reach + atomizer, the
  // bundle they were sold. Collect is deliberately NOT in that fallback: it
  // shipped after entitlements, so nobody holds it implicitly and it must be
  // granted explicitly.
  const granted =
    products === undefined || products === null
      ? (['reach', 'atomizer'] as ProductId[])
      : PRODUCT_IDS.filter((p) => products.some((e) => e.product === p && e.status === 'active'));
  // TinyCMS is one shell (the atomizer id). A `tinycms` grant is that shell.
  // The Atomizer add-on stays a separate check against the atomizer product.
  const entitled: ProductId[] = granted.filter((p) => p !== 'tinycms');
  if (
    (granted.includes('tinycms') || granted.includes('atomizer')) &&
    !entitled.includes('atomizer')
  ) {
    entitled.push('atomizer');
  }

  const saved = toProductId(localStorage.getItem(STORAGE_KEY));
  const initial: ProductId = pin ?? saved ?? 'reach';
  const [product, setProductState] = useState<ProductId>(initial);

  const active = pin ?? product;

  // Drive the whole design system off the root element: every brand token in
  // src/styles/brands.css keys on data-product, so the app re-themes wholesale
  // (grounds, panels, accents, radii, display face) without component edits.
  useEffect(() => {
    document.documentElement.setAttribute('data-product', active);
    document.title = PRODUCT_NAMES[active];
    // TinyCMS has its own mark (the swallow badge) for the tab and home screen;
    // the other shells keep the default icons from public/index.html.
    const tiny = active === 'tinycms' || active === 'atomizer';
    const base = process.env.PUBLIC_URL || '';
    setIconHref('icon', tiny ? `${base}/brand/tinycms/swallow-badge.svg` : `${base}/favicon.ico`);
    setIconHref(
      'apple-touch-icon',
      tiny ? `${base}/brand/tinycms/apple-touch-icon-180.png` : `${base}/logo192.png`
    );
  }, [active]);

  // Once entitlements arrive, make sure the active shell is one the account
  // actually has (e.g. saved 'reach' but the account is atomizer-only).
  useEffect(() => {
    if (loading || pin || entitled.length === 0) return;
    if (!entitled.includes(product)) {
      setProductState(entitled[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, entitled.join(','), product, pin]);

  const setProduct = (next: ProductId) => {
    if (pin) return;
    localStorage.setItem(STORAGE_KEY, next);
    setProductState(next);
  };

  return (
    <ProductContext.Provider
      value={{
        product: active,
        entitled,
        isLoading: isAuthenticated && loading,
        isPinned: pin !== null,
        setProduct,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProduct = (): ProductContextValue => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

/** Point the document's <link rel={rel}> at href (creating it if missing). */
function setIconHref(rel: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
}
